import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ConfirmationModal,
  CREATE_API_TOKEN,
  GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER_IF_HAS_VERSION,
  GET_API_TOKENS,
  LoadingSpinner,
  REVOKE_API_TOKEN,
} from '@employ-engage/common-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type ApiTokenRow = {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  templateIds: string[];
  allowedIps: string[];
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
  revokedAt?: string | null;
};

type TemplateOption = { id: string; name: string };

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const formatDay = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : '—';

/** A token with no templates named reads every one. See token-access.ts. */
const describeTemplates = (
  token: ApiTokenRow,
  templatesById: Map<string, string>
) => {
  if (!token.templateIds?.length) return 'All';
  return token.templateIds.map((id) => templatesById.get(id) ?? id).join(', ');
};

const feedBaseUrl = `${
  import.meta.env.VITE_API_ENDPOINT || ''
}/integration-api/v1`;

/**
 * Where a company owner creates the tokens their BI tool (Qlik, Power BI, a
 * warehouse job) uses to read their own data.
 */
export function DataFeedSettings() {
  const { t } = useTranslation(['common']);
  const [name, setName] = useState('');
  const [includeEmployees, setIncludeEmployees] = useState(true);
  const [templateIds, setTemplateIds] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiTokenRow | null>(null);

  const {
    data,
    loading,
    refetch,
    error: loadError,
  } = useQuery(GET_API_TOKENS, {
    fetchPolicy: 'cache-and-network',
  });
  const [createApiToken, { loading: creating }] = useMutation(CREATE_API_TOKEN);
  const [revokeApiToken] = useMutation(REVOKE_API_TOKEN);

  const { data: templateData } = useQuery(
    GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER_IF_HAS_VERSION
  );

  const tokens: ApiTokenRow[] = data?.apiTokens ?? [];
  // Only templates with a published version: a template nobody has sent has no
  // responses to scope a token to.
  const templates: TemplateOption[] =
    templateData?.getFormTemplatesForCurrentUserOnlyIfTheyHaveVersions ?? [];
  const templatesById = new Map(templates.map((t) => [t.id, t.name]));

  const create = async () => {
    setError(null);
    try {
      const scopes = [
        'responses:read',
        ...(includeEmployees ? ['employees:read'] : []),
      ];
      const { data } = await createApiToken({
        variables: {
          name,
          scopes,
          // An empty list means every template, which is the default and what
          // every token minted before this control existed has.
          templateIds,
          allowedIps: allowedIps
            .split(',')
            .map((ip) => ip.trim())
            .filter(Boolean),
          // A bare date is read as midnight UTC; the token should live out the
          // day the owner picked, in their own timezone.
          expiresAt: expiresOn
            ? new Date(`${expiresOn}T23:59:59`).toISOString()
            : null,
        },
      });
      // The only time the server will ever hand this back.
      setNewToken(data.createApiToken.token);
      setName('');
      setTemplateIds([]);
      setExpiresOn('');
      setAllowedIps('');
      setCopied(false);
      await refetch();
    } catch (err: any) {
      setError(err?.message ?? t("Couldn't create the token."));
    }
  };

  const toggleTemplate = (id: string) =>
    setTemplateIds((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id]
    );

  const revoke = async (token: ApiTokenRow) => {
    setError(null);
    try {
      await revokeApiToken({ variables: { id: token.id } });
      await refetch();
    } catch (err: any) {
      setError(err?.message ?? t("Couldn't revoke the token."));
    }
  };

  if (loading && !data) return <LoadingSpinner spinnerId="data-feed-loading" />;

  if (loadError) {
    return (
      <p className="mt-6 text-sm text-red-500" role="alert">
        {loadError.message}
      </p>
    );
  }

  return (
    <div className="mt-6 space-y-8">
      <section>
        <h2 className="text-base font-semibold text-light-primaryText">
          {t('Data feed')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-light-secondaryText">
          {t('data-feed-description')}
        </p>
        <p className="mt-2 text-sm">
          <span className="text-light-secondaryText">{t('Endpoint')}: </span>
          <code className="rounded bg-gray-100 px-1.5 py-0.5">
            {feedBaseUrl}
          </code>
        </p>
      </section>

      {newToken && (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-gray-900">
            {t('Copy this token now')}
          </h3>
          <p className="mt-1 text-sm text-gray-700">{t('token-shown-once')}</p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-white px-2 py-1 text-sm">
              {newToken}
            </code>
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                navigator.clipboard?.writeText(newToken);
                setCopied(true);
              }}
            >
              {copied ? t('Copied') : t('Copy')}
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setNewToken(null)}
            >
              {t('Done')}
            </Button>
          </div>
        </section>
      )}

      <section>
        <h3 className="text-sm font-semibold text-light-primaryText">
          {t('Create a token')}
        </h3>
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor="tokenName"
              className="block text-sm text-light-secondaryText"
            >
              {t('Name')}
            </label>
            <input
              id="tokenName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('e.g. Qlik Sense')}
              className="mt-1 block w-64 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="tokenExpiry"
              className="block text-sm text-light-secondaryText"
            >
              {t('Expires')}
            </label>
            <input
              id="tokenExpiry"
              type="date"
              value={expiresOn}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              onChange={(e) => setExpiresOn(e.target.value)}
              className="mt-1 block w-48 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-light-primaryText">
            <input
              type="checkbox"
              checked={includeEmployees}
              onChange={(e) => setIncludeEmployees(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
            {t('Include the employee list')}
          </label>
          <Button
            variant="primary"
            onClick={create}
            disabled={creating || !name.trim()}
          >
            {t('Create token')}
          </Button>
        </div>

        <div className="mt-4 max-w-2xl">
          <span className="block text-sm text-light-secondaryText">
            {t('Surveys this token may read')}
          </span>
          <p className="mt-1 text-xs text-light-secondaryText">
            {t(
              'Leave every box unticked to let the token read all surveys. Tick some to restrict it to those.'
            )}
          </p>
          {templates.length === 0 ? (
            <p className="mt-2 text-sm text-light-secondaryText">
              {t('No published surveys yet.')}
            </p>
          ) : (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 p-2">
              {templates.map((template) => (
                <label
                  key={template.id}
                  className="flex items-center gap-2 py-1 text-sm text-light-primaryText"
                >
                  <input
                    type="checkbox"
                    checked={templateIds.includes(template.id)}
                    onChange={() => toggleTemplate(template.id)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  />
                  {template.name}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 max-w-2xl">
          <label
            htmlFor="tokenIps"
            className="block text-sm text-light-secondaryText"
          >
            {t('Allowed IP addresses (optional)')}
          </label>
          <input
            id="tokenIps"
            value={allowedIps}
            onChange={(e) => setAllowedIps(e.target.value)}
            placeholder={t('e.g. 203.0.113.4, 198.51.100.0/24')}
            className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-light-secondaryText">
            {t(
              'Leave blank to use your company IP allowlist, if you have one set.'
            )}
          </p>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-light-primaryText">
          {t('Tokens')}
        </h3>
        {tokens.length === 0 ? (
          <p className="mt-2 text-sm text-light-secondaryText">
            {t('No tokens yet.')}
          </p>
        ) : (
          <table className="mt-2 w-full text-left text-sm">
            <thead className="text-xs uppercase text-light-secondaryText">
              <tr>
                <th className="py-2">{t('Name')}</th>
                <th className="py-2">{t('Token')}</th>
                <th className="py-2">{t('Scopes')}</th>
                <th className="py-2">{t('Surveys')}</th>
                <th className="py-2">{t('Expires')}</th>
                <th className="py-2">{t('Created')}</th>
                <th className="py-2">{t('Last used')}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id} className="border-t border-gray-200">
                  <td className="py-2">{token.name}</td>
                  {/* Only ever the prefix: the rest is not stored. */}
                  <td className="py-2">
                    <code>{token.tokenPrefix}…</code>
                  </td>
                  <td className="py-2">{token.scopes?.join(', ') || '—'}</td>
                  <td className="py-2 max-w-xs truncate">
                    {describeTemplates(token, templatesById)}
                  </td>
                  <td className="py-2">{formatDay(token.expiresAt)}</td>
                  <td className="py-2">{formatDate(token.createdAt)}</td>
                  <td className="py-2">{formatDate(token.lastUsedAt)}</td>
                  <td className="py-2 text-right">
                    {token.revokedAt ? (
                      <span className="text-light-secondaryText">
                        {t('Revoked')}
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => setTokenToRevoke(token)}
                      >
                        {t('Revoke')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ConfirmationModal
        open={!!tokenToRevoke}
        setOpen={(open) => !open && setTokenToRevoke(null)}
        text={t('revoke-token-confirm', { name: tokenToRevoke?.name ?? '' })}
        warningText={t('revoke-token-warning')}
        confirmButtonText={t('Revoke')}
        onConfirm={() => {
          const token = tokenToRevoke;
          setTokenToRevoke(null);
          if (token) revoke(token);
        }}
        onCancel={() => setTokenToRevoke(null)}
      />
    </div>
  );
}

export default DataFeedSettings;
